import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAll } from '../../services/helpers';
import api from '../../services';
import * as tagService from '../../services/tags';

// ZET-4795: hidden wrapper offers for linkout-family offer actions. Site operators
// manage these on the Offer Actions screen, never as offers — so they are filtered
// out of every offer dropdown-list and only surface in the sequence builder's
// Destinations tab (which filters *for* this type).
export const DESTINATION_OFFER_TYPE_ID = 11;

// 6 Return Url, 8 Landing Page, 9 Offer Block, 10 Dynamic Offer Section, 11 Destination
const EXCLUDED_OFFER_TYPES = [6, 8, 9, 10, DESTINATION_OFFER_TYPE_ID];

const fetchAllOfferVariations = async () => {
  try {
    const response = await api.get('offervariations');
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

const fetchOfferVariations = async (id) => {
  try {
    const response = await api.get(`offervariations/by-offer/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

const fetchOffer = async (id) => {
  try {
    const response = await api.get(`offers/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

const fetchCriteriaByOfferId = async (offerId) => {
  try {
    const response = await api.get(`criteria/offer/${offerId}`);
    return response.data;
  } catch (error) {
    return null;
  }
};

const createOfferVariation = async ({ offerId, name, flex }) => {
  try {
    const params = new URLSearchParams({
      offerId: offerId,
      name: name,
      flex: flex,
    }).toString();

    const response = await api.post(`/offervariations/create-from-existing?${params}`);
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const useOffersTable = (excludedOfferTypes = EXCLUDED_OFFER_TYPES) =>
  useQuery({
    queryKey: ['offerSummaries'],
    queryFn: () => fetchAll('offers/summaries'),
    select: (data) =>
      data
        .filter((item) => !excludedOfferTypes.includes(item.offerType.id))
        .filter((item) => item.isPrimaryVariant === true || item.isPrimaryVariant == null),
  });

export const useOfferSummaries = (excludedOfferTypes = EXCLUDED_OFFER_TYPES) =>
  useQuery({
    queryKey: ['offerSummaries'],
    queryFn: () => fetchAll('offers/summaries'),
    select: (data) => data.filter((item) => !excludedOfferTypes.includes(item.offerType.id)),
  });

export const useOfferVariantsByPrimaryOfferId = (primaryOfferId, enabled = false) =>
  useQuery({
    queryKey: ['offerVariants', primaryOfferId],
    queryFn: () => fetchOfferVariations(primaryOfferId),
    enabled,
  });

export const useOffer = (id, enabled = true) =>
  useQuery({
    queryKey: ['offer', id],
    queryFn: () => fetchOffer(id),
    enabled,
  });

export const useOfferWithVariationContext = (id, enabled = true) =>
  useQuery({
    queryKey: ['offerWithContext', id],
    queryFn: async () => {
      const response = await api.get(`offers/${id}/with-variation-context`);
      return response.data;
    },
    enabled: enabled && !!id && id !== 'create',
  });

export const useCriteriaByOfferId = (offerId, enabled = true) =>
  useQuery({
    queryKey: ['criteria', 'offer', offerId],
    queryFn: () => fetchCriteriaByOfferId(offerId),
    enabled: enabled && !!offerId,
  });

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => api.put(`offers/${data.id}`, data),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['offer', data.id] });
    },
  });
};

export const useCreateOfferVariation = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createOfferVariation,
    onSuccess: (...args) => {
      queryClient.invalidateQueries('offerVariants');
      if (onSuccess) {
        onSuccess(...args);
      }
    },
  });
};

export const useDeleteOfferVariationByOfferId = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`offervariations/by-offer/${id}`),
    onSuccess: (...args) => {
      queryClient.invalidateQueries('offerVariants');
      if (onSuccess) {
        onSuccess(...args);
      }
    },
  });
};

export const usePromoteOfferVariationByOfferId = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ([primaryOfferId, promotedOfferId]) =>
      api.put(`offervariations/by-offer/promote/${primaryOfferId}/${promotedOfferId}`),
    onSuccess: (...args) => {
      queryClient.invalidateQueries('offerVariants');
      queryClient.invalidateQueries('offer');
      if (onSuccess) {
        onSuccess(...args);
      }
    },
  });
};

export const useOfferQuestions = ({ enabled = false }) =>
  useQuery({
    queryKey: ['offerQuestions'],
    queryFn: () =>
      api.get('offerquestions').then((res) => {
        return res.data.map((a) => {
          return {
            id: a.question.id,
            name: a.offer.name,
            question: a.question.question,
          };
        });
      }),
    enabled,
  });

export const useQuestionAnswers = (questionId) =>
  useQuery({
    queryKey: ['questionAnswers', questionId],
    queryFn: () => api.get(`offerquestionanswer/question/${questionId}`).then((res) => res.data),
    enabled: !!questionId,
  });

export const useOfferVariations = () =>
  useQuery({
    queryKey: ['offerVariations'],
    queryFn: fetchAllOfferVariations,
  });

export const useExpandableOffers = () => {
  return useQuery({
    queryKey: ['expandableOffers'],
    queryFn: () => fetchAll('offers/expandable-table'),
    select: (data) => ({
      ...data,
      offers: data.filter((offer) => !EXCLUDED_OFFER_TYPES.includes(offer.offerType?.id)),
    }),
    refetchOnWindowFocus: false,
    onError: (error) => {
      console.error('Error fetching expandable offers:', error);
    },
  });
};

export const useUpdateOfferStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, activeStatus, zuulUser }) => {
      const saveData = {
        id,
        isActive: activeStatus,
        active: activeStatus,
        zuulUser,
      };
      return api.put(`offers/active/${id}`, saveData);
    },
    onMutate: async ({ id, activeStatus }) => {
      await queryClient.cancelQueries({ queryKey: ['expandableOffers'] });
      const previousData = queryClient.getQueryData(['expandableOffers']);
      queryClient.setQueryData(['expandableOffers'], (old) => {
        if (!old?.offers) return old;

        return {
          ...old,
          offers: old.offers.map((offer) => {
            if (offer.id === id) {
              return { ...offer, isActive: activeStatus };
            }
            if (offer.variations) {
              return {
                ...offer,
                variations: offer.variations.map((variation) =>
                  variation.id === id ? { ...variation, isActive: activeStatus } : variation
                ),
              };
            }
            return offer;
          }),
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['expandableOffers'], context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['expandableOffers'] });
      queryClient.invalidateQueries({ queryKey: ['offersTable'] });
    },
  });
};

export const useAdvertisers = () =>
  useQuery({
    queryKey: ['advertisers'],
    queryFn: async () => {
      const response = await api.get('advertisers');
      return response.data.map((a) => ({
        id: a.id,
        name: a.name,
        ariaAdvertiserId: a.ariaAdvertiserId,
      }));
    },
    refetchOnWindowFocus: false,
  });

export const useTags = () =>
  useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const allTags = await tagService.getTags();
      return (allTags || []).filter((tag) => tag.type && tag.type.name !== 'domain' && tag.type.name !== 'affiliate');
    },
    refetchOnWindowFocus: false,
  });

export const useRedirectDomains = () =>
  useQuery({
    queryKey: ['redirectDomains'],
    queryFn: async () => {
      const response = await api.get('domains/redirects');
      return response.data.map((domain) => ({
        id: domain.id,
        name: domain.name,
      }));
    },
    refetchOnWindowFocus: false,
  });

export const useFeatureToggle = (featureName) =>
  useQuery({
    queryKey: ['featureToggle', featureName],
    queryFn: async () => {
      const response = await api.get('features');
      const feature = response.data.find((f) => f.name === featureName);
      return feature?.enabled || false;
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const useFlexByOfferID = (offerId, enabled = true) =>
  useQuery({
    queryKey: ['flexByOfferID', offerId],
    queryFn: async () => {
      const response = await api.get(`offervariations/by-offer/${offerId}`);
      const variations = response.data;
      const currentVariation = variations.find((v) => v.offer?.id === offerId);
      return currentVariation?.flex;
    },
    enabled: enabled && !!offerId,
  });
